import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { cldPresets } from '../lib/cloudinary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const Cart = ({ cartItems, updateQuantity, removeFromCart }) => {
  const navigate = useNavigate();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const calculatedTotal = cartItems.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    setTotal(calculatedTotal);
  }, [cartItems]);

  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    navigate('/checkout');
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <ShoppingBag className="h-24 w-24 mx-auto mb-6 text-gray-300" />
          <h2 className="text-3xl font-bold mb-4 text-gray-700">Your Cart is Empty</h2>
          <p className="text-gray-600 mb-8">Add some amazing furniture to your cart!</p>
          <Button
            onClick={() => navigate('/shop')}
            size="lg"
            className="text-white font-semibold hover:scale-105 transition-transform duration-300"
            style={{ backgroundColor: '#00CED1' }}
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-2">
          Shopping <span style={{ color: '#00CED1' }}>Cart</span>
        </h1>
        <p className="text-gray-600 mb-8">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'} in your cart</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item) => (
              <Card key={item.id} className="overflow-hidden border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Product Image */}
                    <div className="w-full sm:w-32 h-32 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={cldPresets.cartItem(resolveImageUrl(item.image))}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                        data-testid={`cart-item-image-${item.id}`}
                      />
                    </div>

                    {/* Product Details */}
                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-bold mb-1" data-testid={`cart-item-name-${item.id}`}>{item.name}</h3>
                          <p className="text-sm text-gray-600 capitalize">{item.category}</p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors duration-300"
                          data-testid={`cart-item-remove-${item.id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>

                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {item.description}
                      </p>

                      <div className="flex justify-between items-center">
                        {/* Quantity Controls */}
                        <div className="flex items-center space-x-3">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            className="h-8 w-8"
                            style={{ borderColor: '#00CED1' }}
                            data-testid={`cart-item-qty-decrement-${item.id}`}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="font-semibold w-8 text-center" data-testid={`cart-item-qty-${item.id}`}>{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="h-8 w-8"
                            style={{ borderColor: '#00CED1' }}
                            data-testid={`cart-item-qty-increment-${item.id}`}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: '#00CED1' }}>
                            R{(item.price * item.quantity).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">R{item.price.toFixed(2)} each</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>R{total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>{total >= 10000 ? 'FREE' : 'R50.00'}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax (15% VAT)</span>
                    <span>R{(total * 0.15).toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">Total</span>
                      <span className="text-3xl font-bold" style={{ color: '#00CED1' }}>
                        R{(total + (total >= 10000 ? 0 : 50) + total * 0.15).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {total >= 10000 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-green-700 font-semibold">
                      🎉 You qualify for FREE shipping!
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  size="lg"
                  className="w-full text-white font-semibold hover:scale-105 transition-transform duration-300"
                  style={{ backgroundColor: '#00CED1' }}
                  data-testid="cart-checkout-btn"
                >
                  Proceed to Checkout <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <Button
                  onClick={() => navigate('/shop')}
                  variant="outline"
                  size="lg"
                  className="w-full mt-3 font-semibold"
                  style={{ borderColor: '#00CED1', color: '#00CED1' }}
                  data-testid="cart-continue-shopping-btn"
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
